import React, { useState, useEffect } from 'react';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../../components/ui/select';
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../../components/ui/dialog';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Search } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { adminAPI } from '../../lib/api';
import { formatPrice } from '../../lib/utils';
import { toast } from 'sonner';

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showAdd, setShowAdd] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '',
    slug: '',
    category: '',
    image: '',
    price_cents: '',
    stock: '',
    is_active: true
  });
  const [editProduct, setEditProduct] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [looks, setLooks] = useState([]);
  const [selectedLooks, setSelectedLooks] = useState([]);
  const [editSelectedLooks, setEditSelectedLooks] = useState([]);

  useEffect(() => {
    loadProducts();
    loadLooks();
  }, [page]);

  const loadLooks = async () => {
    try {
      const response = await adminAPI.getLooks({ page: 1, limit: 100 });
      setLooks(response.data.items);
    } catch (error) {
      setLooks([]);
    }
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getProducts({ page, limit: 20 });
      setProducts(response.data.items);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce produit ?')) return;
    try {
      await adminAPI.deleteProduct(id);
      toast.success('Produit supprimé');
      loadProducts();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    try {
      const productRes = await adminAPI.createProduct({
        name: newProduct.name,
        slug: newProduct.slug,
        category: newProduct.category,
        images: newProduct.image ? [newProduct.image] : [],
        variants: [{
          sku: newProduct.slug,
          price_cents: Number(newProduct.price_cents),
          stock: Number(newProduct.stock)
        }],
        is_active: newProduct.is_active
      });
      // Ajout du produit aux looks sélectionnés
      await Promise.all(selectedLooks.map(async (lookId) => {
        const look = looks.find(l => l.id === lookId);
        if (look) {
          const items = Array.isArray(look.items) ? [...look.items] : [];
          items.push({ product_id: productRes.data.id, position: items.length });
          await adminAPI.updateLook(lookId, { ...look, items });
        }
      }));
      toast.success('Produit ajouté');
      setShowAdd(false);
      setNewProduct({ name: '', slug: '', category: '', image: '', price_cents: '', stock: '', is_active: true });
      setSelectedLooks([]);
      loadProducts();
      loadLooks();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleEditProduct = (product) => {
    setEditProduct({
      ...product,
      image: product.images?.[0] || '',
      price_cents: product.variants?.[0]?.price_cents || '',
      stock: product.variants?.[0]?.stock || '',
    });
    setShowEdit(true);
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    try {
      await adminAPI.updateProduct(editProduct.id, {
        name: editProduct.name,
        slug: editProduct.slug,
        category: editProduct.category,
        images: editProduct.image ? [editProduct.image] : [],
        variants: [{
          sku: editProduct.slug,
          price_cents: Number(editProduct.price_cents),
          stock: Number(editProduct.stock)
        }],
        is_active: editProduct.is_active
      });
      toast.success('Produit modifié');
      setShowEdit(false);
      setEditProduct(null);
      loadProducts();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-products">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Produits</h1>
          <p className="text-muted-foreground">Gérez votre catalogue de produits</p>
        </div>
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Nouveau produit
            </Button>
          </DialogTrigger>
          <DialogContent>
            <form onSubmit={handleAddProduct} className="space-y-4">
              <DialogHeader>
                <DialogTitle>Ajouter un produit</DialogTitle>
              </DialogHeader>
              <Input required placeholder="Nom" value={newProduct.name} onChange={e => setNewProduct(p => ({ ...p, name: e.target.value }))} />
              <Input required placeholder="Slug (unique)" value={newProduct.slug} onChange={e => setNewProduct(p => ({ ...p, slug: e.target.value }))} />
              <Input required placeholder="Catégorie" value={newProduct.category} onChange={e => setNewProduct(p => ({ ...p, category: e.target.value }))} />
              <Input required placeholder="Image (URL)" value={newProduct.image} onChange={e => setNewProduct(p => ({ ...p, image: e.target.value }))} />
              <Input required type="number" placeholder="Prix (centimes)" value={newProduct.price_cents} onChange={e => setNewProduct(p => ({ ...p, price_cents: e.target.value }))} />
              <Input required type="number" placeholder="Stock" value={newProduct.stock} onChange={e => setNewProduct(p => ({ ...p, stock: e.target.value }))} />
              <div className="flex gap-2 items-center">
                <label className="text-sm">Actif</label>
                <input type="checkbox" checked={newProduct.is_active} onChange={e => setNewProduct(p => ({ ...p, is_active: e.target.checked }))} />
              </div>
              <div>
                <label className="block text-sm mb-1">Associer à un ou plusieurs looks</label>
                <Select multiple value={selectedLooks} onValueChange={setSelectedLooks}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Choisir les looks" />
                  </SelectTrigger>
                  <SelectContent>
                    {looks.map(look => (
                      <SelectItem key={look.id} value={look.id}>{look.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button type="button" variant="secondary">Annuler</Button>
                </DialogClose>
                <Button type="submit">Ajouter</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Rechercher un produit..." className="pl-10" />
          </div>
        </CardContent>
      </Card>

      {/* Products Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Produit</TableHead>
                <TableHead>Catégorie</TableHead>
                <TableHead>Prix</TableHead>
                <TableHead>Stock</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><div className="h-4 bg-secondary animate-pulse rounded w-32" /></TableCell>
                    <TableCell><div className="h-4 bg-secondary animate-pulse rounded w-20" /></TableCell>
                    <TableCell><div className="h-4 bg-secondary animate-pulse rounded w-16" /></TableCell>
                    <TableCell><div className="h-4 bg-secondary animate-pulse rounded w-12" /></TableCell>
                    <TableCell><div className="h-4 bg-secondary animate-pulse rounded w-16" /></TableCell>
                    <TableCell><div className="h-4 bg-secondary animate-pulse rounded w-16 ml-auto" /></TableCell>
                  </TableRow>
                ))
              ) : products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    Aucun produit trouvé
                  </TableCell>
                </TableRow>
              ) : (
                products.map((product, index) => (
                  <motion.tr
                    key={product.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.05 }}
                    className="table-row-hover"
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-14 bg-secondary rounded overflow-hidden flex-shrink-0">
                          {product.images?.[0] && (
                            <img
                              src={product.images[0]}
                              alt={product.name}
                              className="w-full h-full object-cover"
                            />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{product.name}</p>
                          <p className="text-xs text-muted-foreground font-mono">{product.slug}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{product.category}</Badge>
                    </TableCell>
                    <TableCell className="font-mono">
                      {product.variants?.[0] 
                        ? formatPrice(product.variants[0].price_cents)
                        : '-'
                      }
                    </TableCell>
                    <TableCell>
                      {product.variants?.[0]?.stock ?? 0}
                    </TableCell>
                    <TableCell>
                      <Badge variant={product.is_active ? 'default' : 'secondary'}>
                        {product.is_active ? 'Actif' : 'Inactif'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleEditProduct(product)} title="Éditer">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => window.open(`/produit/${product.slug}`, '_blank')}
                          title="Voir la fiche"
                        >
                          <span className="sr-only">Voir</span>
                          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="3.5"/><path d="M2 12S5.5 5 12 5s10 7 10 7-3.5 7-10 7S2 12 2 12z"/></svg>
                        </Button>
                              {/* Modal édition produit */}
                              <Dialog open={showEdit} onOpenChange={setShowEdit}>
                                <DialogContent>
                                  {editProduct && (
                                    <form onSubmit={handleUpdateProduct} className="space-y-4">
                                      <DialogHeader>
                                        <DialogTitle>Modifier le produit</DialogTitle>
                                      </DialogHeader>
                                      <Input required placeholder="Nom" value={editProduct.name} onChange={e => setEditProduct(p => ({ ...p, name: e.target.value }))} />
                                      <Input required placeholder="Slug (unique)" value={editProduct.slug} onChange={e => setEditProduct(p => ({ ...p, slug: e.target.value }))} />
                                      <Input required placeholder="Catégorie" value={editProduct.category} onChange={e => setEditProduct(p => ({ ...p, category: e.target.value }))} />
                                      <Input required placeholder="Image (URL)" value={editProduct.image} onChange={e => setEditProduct(p => ({ ...p, image: e.target.value }))} />
                                      <Input required type="number" placeholder="Prix (centimes)" value={editProduct.price_cents} onChange={e => setEditProduct(p => ({ ...p, price_cents: e.target.value }))} />
                                      <Input required type="number" placeholder="Stock" value={editProduct.stock} onChange={e => setEditProduct(p => ({ ...p, stock: e.target.value }))} />
                                      <div className="flex gap-2 items-center">
                                        <label className="text-sm">Actif</label>
                                        <input type="checkbox" checked={editProduct.is_active} onChange={e => setEditProduct(p => ({ ...p, is_active: e.target.checked }))} />
                                      </div>
                                      <DialogFooter>
                                        <DialogClose asChild>
                                          <Button type="button" variant="secondary">Annuler</Button>
                                        </DialogClose>
                                        <Button type="submit">Enregistrer</Button>
                                      </DialogFooter>
                                    </form>
                                  )}
                                </DialogContent>
                              </Dialog>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDelete(product.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </motion.tr>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Précédent
          </Button>
          <span className="flex items-center px-4 text-sm text-muted-foreground">
            Page {page} sur {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Suivant
          </Button>
        </div>
      )}
    </div>
  );
}
